import { columnIcons } from './column-icons'
import Checkbox from '@/components/checkbox/Checkbox'
import Visibility from '@/assets/icons/table-visibility.svg?react'
import { IconButton } from '@/components/icon-button/IconButton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { ColumnKind, TableColumn } from '../schemas'
import { orderedColumns, TableLayout } from '../layout'

export default function TableColumns({
  columns,
  layout,
  disabled,
  minimumVisible = 0,
  onChange,
  onOpen,
}: {
  columns: Pick<TableColumn, 'id' | 'name' | 'kind' | 'source_file'>[]
  layout: TableLayout
  minimumVisible?: number
  disabled: boolean
  onChange: (update: (layout: TableLayout) => TableLayout) => void
  onOpen?: () => void
}) {
  const selectedCount = columns.filter((column) => !layout.hidden.includes(column.id)).length
  const hasHiddenColumns = selectedCount < columns.length
  const byId = new Map(columns.map((column) => [column.id, column]))
  const orderedIds = orderedColumns(
    columns.map((column) => column.id),
    layout
  )
  const allSelected = columns.length > 0 && !hasHiddenColumns
  return (
    <Popover>
      <PopoverTrigger asChild>
        <IconButton
          size="sm"
          variant="plain"
          color={hasHiddenColumns ? 'primary' : 'black'}
          icon={
            <span className="relative">
              <Visibility className="h-4 w-4" />
              {hasHiddenColumns && <span className="absolute -right-[3.5px] -top-[3.5px] h-1.5 w-1.5 rounded-full bg-current" />}
            </span>
          }
          className={`disabled:!text-true-gray-200 ${hasHiddenColumns ? 'bg-brand-50' : ''}`}
          aria-label={hasHiddenColumns ? `Columns · ${selectedCount}` : 'Columns'}
          aria-pressed={hasHiddenColumns}
          tooltipText="Hide columns"
          disabled={disabled}
          onClick={onOpen}
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        width="auto"
        showArrow={false}
        className="w-[280px] max-w-[calc(100vw-32px)] border border-brand-neutral-200 p-4 shadow-menu"
        aria-label="Visible columns"
      >
        <p className="mb-3 text-sm font-medium">Columns</p>
        <div className="-mx-1 flex max-h-[50vh] flex-col overflow-y-auto px-1">
          {orderedIds.map((id) => {
            const column = byId.get(id)
            const KindIcon = column?.source_file ? columnIcons.file : columnIcons[column?.kind ?? ColumnKind.Text]
            return (
              <label key={id} className="flex cursor-pointer items-center gap-3 py-3 text-sm text-black">
                <Checkbox
                  checked={!layout.hidden.includes(id)}
                  disabled={!layout.hidden.includes(id) && selectedCount <= minimumVisible}
                  onCheckedChange={() =>
                    onChange((current) => ({
                      ...current,
                      hidden: current.hidden.includes(id) ? current.hidden.filter((value) => value !== id) : [...current.hidden, id],
                    }))
                  }
                />
                <KindIcon aria-hidden className="h-4 w-4 shrink-0 text-brand-neutral-600" />
                <span className="min-w-0 truncate">{column?.source_file ? 'File' : column?.name}</span>
              </label>
            )
          })}
        </div>
        <div className="-mx-4 -mb-4 mt-3 border-t border-brand-neutral-200 px-4 py-4">
          <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-brand-500">
            <Checkbox
              checked={allSelected ? true : selectedCount > 0 ? 'indeterminate' : false}
              disabled={!columns.length || (allSelected && columns.length <= minimumVisible)}
              onCheckedChange={() =>
                onChange((current) => ({
                  ...current,
                  hidden: allSelected ? orderedIds.slice(minimumVisible) : [],
                }))
              }
            />
            Select all
          </label>
        </div>
      </PopoverContent>
    </Popover>
  )
}
